import { Injectable, OnDestroy } from '@angular/core'
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router'
import { TranslocoService } from '@ngneat/transloco'
import { BehaviorSubject, Observable } from 'rxjs'
import { SubSink } from 'subsink'
import { ApiService } from './api.service'
import { EventService } from './event.service'
import { FirestoreService2 } from './firestore.service2'
import { getFromPreRender, GlobalService } from './global.service'
import { parseMenuTree, use } from './utils'

@Injectable({ providedIn: 'root' })
export class MenusService implements OnDestroy {

  private rawMenus: any[] = null
  private menus: { [name: string]: BehaviorSubject<any[]> } = {}
  private activeProfile: string = null
  private loadedMenus = false
  private fss: FirestoreService2
  private subs = new SubSink()

  constructor(private g: GlobalService, private transloco: TranslocoService) {
    this.subs.add(
      EventService.get('reload-menus').subscribe(() => {
        this.updateMenus()
      }),
      // Reset menus when program changes
      EventService.get('program-changed').subscribe(() => {
        this.rawMenus = null
        this.activeProfile = null
        this.loadedMenus = false
        // Clear all menu subjects
        for (const menuName in this.menus) {
          this.menus[menuName].next([])
        }
      })
    )
  }

  ngOnDestroy() {
    this.subs.unsubscribe()
  }

  /******************
   * PRIVATE METHODS
   ******************/

  /**
   * Updates the menus that are being listened to in components
   */
  updateMenus() {
    if (this.rawMenus) {
      for (const menuName in this.menus) {
        if (menuName === 'root') this.menus[menuName].next(this.getACLMenus(''))
        else {
          const menu = this.rawMenus.find(m => m.name === menuName) || null
          if (menu) this.menus[menuName].next(this.getACLMenus(menu.id))
        }
      }
    }
  }

  /**
   * Replaces special TAGS in routes at runtime
   * Available tags
   * [uid] => this.g.user.uid
   * [type] => this.g.type
   * @private
   */
  private replaceTags(menus: any[]) {
    return menus.length ? menus.map(m => ({
      ...m, route: m.route ? m.route
        .replace('[type]', this.g.type)
        .replace('[email]', this.g.user.email)
        .replace('[username]', this.g.user[this.g.program.username])
        .replace('[uid]', this.g.user.uid) : m.route
    })) : []
  }

  /**
   * Creates a PrimeNG MenuModel to fill the categories MegaMenu
   * @private
   */
  private getMegaMenuCategories() {
    const menus: any[] = []
    this.g.get$('categories').subscribe((categories: any[]) => {
      // this.fss2.list('categories', { where: [ [ 'active', '==', true ] ] }).subscribe((categories: any[]) => {
      const roots = categories.filter(c => c.parent === 'HOME')
      for (const root of roots) {
        menus.push({ label: root.name, items: [] })
      }

      for (const first of menus) {
        for (const m of categories.filter(c => c.parentId === first.id)) {
          first.items.push([{ label: m.name, items: categories.filter(c => c.parentId === m.id).map(c => ({ label: c.name })) }])
        }
      }

      if (this.menus['rewards-menu']) this.menus['rewards-menu'].next(menus)
      else this.menus['rewards-menu'] = new BehaviorSubject(menus)
    })
  }

  /**
   * Returns an array in the pattern of a PrimeNG Treemenu
   * filtered only with the menus allowed to the user
   * based on their profile (role)
   */
  private getACLMenus(firstParent = ''): any[] {
    if (this.rawMenus && this.g.get('profile')) {
      const userMenus = this.rawMenus.filter(m => (this.g.get('profile').access || []).indexOf(m.id) > -1 && m.show).map(m => ({ ...m, name: this.transloco.translate(m.name) }))
      const menusWithRoutes = this.replaceTags(userMenus)
      const aclMenus = parseMenuTree(menusWithRoutes, [...menusWithRoutes], firstParent, 'name')
      return aclMenus
    }
    return []
  }

  /*****************
   * PUBLIC METHODS
   *****************/

  /**
   * Listens for updates to the user profile to apply ACL rules to menus
   * @param user
   */
  setProfile(user: any) {
    // console.log('# setProfile()', user)
    if (user) {
      if (user.role !== this.activeProfile) {
        const api: ApiService = use<ApiService>(ApiService)
        this.activeProfile = user.role
        // this.fss2.getBy('profiles', 'role', user.role).subscribe(prof => {
        api.post('db/action', {
          action: 'profileByRole',
          role: user.role,
          nick: this.g.nick
        }, true).subscribe(prof => {
          this.g.set('profile', prof, true)
          this.updateMenus()
        })
      }
    }
  }

  /**
   * Pre-loads the menus and stores them in GlobalService
   */
  loadMenus() {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    if (!this.rawMenus && !this.loadedMenus) {
      getFromPreRender('menus', this.fss.list('menus', { api: false, nick: 'yes', where: [['active', '==', true]] })).subscribe((results: any[]) => {
        this.loadedMenus = true
        const menus = results

        if (menus && menus.length) {
          this.g.set('rawMenus', menus, true)
          this.rawMenus = menus
          const aclMenus: any[] = parseMenuTree(menus, [...menus], '', 'name')
          const menuObj = {}
          for (const parent of aclMenus) if (parent.items && parent.items.length) menuObj[parent.label] = parent.items.map(i => i.label)
          this.g.set('menus', menuObj, true)
          this.updateMenus()
        }
      })
      this.loadedMenus = true
    }
  }

  /**
   * Returns an Observable to listen to a specific menu
   * If parentName is empty or 'root', returns an observable with all root-level menus
   * @param {string} parentName
   * @returns {BehaviorSubject<any>}
   */
  getMenuByParent$(parentName: string): BehaviorSubject<any> {
    if (parentName === 'rewards-menu') this.getMegaMenuCategories()
    if (!this.menus[parentName || 'root'] || this.menus[parentName || 'root'] === undefined) this.menus[parentName || 'root'] = new BehaviorSubject([])
    this.updateMenus()
    return this.menus[parentName || 'root']
  }

  /**
   * Legacy method created by MRS moved to this service
   * Returns an object with FormGroup
   */
  getMenusGrp(): any {
    const menusGrp = {}
    // for (const [ key, value ] of (Object.entries(this.g.get('menus')) as any[])) {
    //   const grp = { enabled: [ false, [ Validators.required as any ] ] }
    //   for (const v of value) {
    //     grp[v] = [ false, [ Validators.required as any ] ]
    //   }
    //   menusGrp[key] = this.g.formBuilder.group(grp)
    // }
    return menusGrp
  }

  /****************************************
   ** Methods for AuthGuard and Security **
   ****************************************/

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const publicUrls = ['/home']
    const profile = this.g.get('profile')
    const user = this.g.get('user')
    const rawMenusWithRoutes = (this.g.get('rawMenus') || []).filter(r => r.route)
    const menusWithReplacedRoutes = this.replaceTags(rawMenusWithRoutes)
    const allowed = profile ? [
      ...menusWithReplacedRoutes.filter(r => (profile.access || []).indexOf(r.id) > -1).map(r => r.route),
      ...publicUrls
    ] : publicUrls

    if ((profile && profile.role === 'admin') || (user && user.role === 'admin')) return true

    return allowed.filter(url => state.url.indexOf(url) > -1).length > 0
  }

}
